import React, { useEffect, useState, Suspense } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Map as MapIcon, Box, Columns3 } from 'lucide-react';
import { ProjectList } from './components/ProjectList';
import { LayerSwitcher } from './components/LayerSwitcher';
import { EnvelopeEditor } from './components/EnvelopeEditor';

const SceneViewer = React.lazy(() => import('./components/SceneViewer').then(m => ({ default: m.SceneViewer })));
const ExportPanel = React.lazy(() => import('./components/ExportPanel').then(m => ({ default: m.ExportPanel })));
import { autoParser } from './lib/parsers';

const LazyFallback = () => <div className="h-full w-full bg-surface" />;
import { UploadPanel } from './components/UploadPanel';
import { MapViewport } from './components/MapViewport';
import { ErrorBoundary } from './components/ErrorBoundary';
import { useUiStore } from './stores/useUiStore';
import { Tooltip } from './components/Tooltip';
import { KeyboardShortcuts } from './components/KeyboardShortcuts';
import { useProjectStore } from './stores/useProjectStore';
import type { ViewMode } from './stores/useUiStore';
import { useAuthStore, selectStatus } from './stores/useAuthStore';
import { getSession, onAuthChange } from './lib/supabase/auth';
import { LoginModal } from './components/LoginModal';
import type { Session } from '@supabase/supabase-js';
import { UserMenu } from './components/UserMenu';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const VIEW_MODES: { id: ViewMode; label: string; icon: React.ReactNode }[] = [
  { id: 'map', label: 'Map', icon: <MapIcon className="h-4 w-4" /> },
  { id: '3d', label: '3D', icon: <Box className="h-4 w-4" /> },
  { id: 'split', label: 'Split', icon: <Columns3 className="h-4 w-4" /> },
];

const ViewModeToggle: React.FC = () => {
  const viewMode = useUiStore((s) => s.viewMode);
  const setViewMode = useUiStore((s) => s.setViewMode);

  return (
    <div className="flex items-center gap-1 rounded-lg bg-slate-800/90 p-1 backdrop-blur-sm border border-slate-600/50 shadow-lg">
      {VIEW_MODES.map((mode) => (
        <Tooltip key={mode.id} label={mode.label} position="bottom">
          <button
            onClick={() => setViewMode(mode.id)}
            className={[
              'flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors min-h-touch',
              viewMode === mode.id
                ? 'bg-sky-900/40 text-sky-300'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/50',
            ].join(' ')}
            aria-pressed={viewMode === mode.id}
          >
            {mode.icon}
            <span className="hidden sm:inline">{mode.label}</span>
          </button>
        </Tooltip>
      ))}
    </div>
  );
};

const AppLayout: React.FC = () => {
  const showProjectPanel = useUiStore((s) => s.showProjectPanel);
  const showLayerPanel = useUiStore((s) => s.showLayerPanel);
  const viewMode = useUiStore((s) => s.viewMode);
  const setViewMode = useUiStore((s) => s.setViewMode);
  const activeSceneId = useProjectStore((s) => s.activeSceneId);

  // Auto-switch view mode based on scene selection
  useEffect(() => {
    if (activeSceneId && viewMode === 'map') {
      setViewMode('3d');
    }
  }, [activeSceneId, viewMode, setViewMode]);

  // Tablet portrait detection: collapse side panels on narrow viewports
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1024px) and (orientation: portrait)');
    const handleChange = (e: MediaQueryListEvent | MediaQueryList) => {
      const isTabletPortrait = e.matches;
      useUiStore.getState().setShowProjectPanel(!isTabletPortrait);
      useUiStore.getState().setShowLayerPanel(!isTabletPortrait);
    };
    handleChange(mq);
    mq.addEventListener('change', handleChange);
    return () => mq.removeEventListener('change', handleChange);
  }, []);

  const [showShortcuts, setShowShortcuts] = useState(false);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.repeat) return;
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === '?' && e.shiftKey) {
        setShowShortcuts((prev) => !prev);
        return;
      }
      switch (e.key.toLowerCase()) {
        case 'm':
          setViewMode('map');
          break;
        case '3':
          setViewMode('3d');
          break;
        case 's':
          setViewMode('split');
          break;
        case 'escape':
          if (showShortcuts) {
            setShowShortcuts(false);
          } else if (useUiStore.getState().showProjectPanel || useUiStore.getState().showLayerPanel) {
            useUiStore.getState().setShowProjectPanel(false);
            useUiStore.getState().setShowLayerPanel(false);
          } else {
            useUiStore.getState().resetCamera();
          }
          break;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [setViewMode, showShortcuts]);

  const [isOnline, setIsOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  const showMap = viewMode === 'map' || viewMode === 'split';
  const show3D = viewMode === '3d' || viewMode === 'split';

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-surface text-white">
      {/* Left Sidebar: Projects + Upload */}
      {showProjectPanel && (
        <aside className="flex shrink-0 flex-col overflow-hidden border-r border-slate-700/50 bg-surface-elevated w-sidebar animate-slide-in-left">
          <div className="shrink-0 border-b border-slate-700/50">
            <UserMenu />
          </div>
          <div className="flex-1 overflow-hidden">
            <ProjectList />
          </div>
          <div className="shrink-0 border-t border-slate-700/50">
            <UploadPanel />
          </div>
        </aside>
      )}

      {/* Main Viewer */}
      <main className="relative flex-1 overflow-hidden">
        {showMap && show3D ? (
          /* Split mode */
          <div className="flex h-full w-full">
            <div className="flex-1 border-r border-slate-700/50">
              <ErrorBoundary>
                <MapViewport />
              </ErrorBoundary>
            </div>
            <div className="flex-1">
              <ErrorBoundary>
                <Suspense fallback={<LazyFallback />}><SceneViewer /></Suspense>
              </ErrorBoundary>
            </div>
          </div>
        ) : showMap ? (
          /* Map mode */
          <ErrorBoundary>
            <MapViewport />
          </ErrorBoundary>
        ) : (
          /* 3D mode */
          <ErrorBoundary>
            <Suspense fallback={<LazyFallback />}><SceneViewer /></Suspense>
          </ErrorBoundary>
        )}

        {/* View mode toggle (floating top-center) */}
        <div className="absolute left-1/2 top-4 z-20 -translate-x-1/2">
          <ViewModeToggle />
        </div>

        {/* Offline indicator */}
        {!isOnline && (
          <div className="absolute left-1/2 top-14 z-50 -translate-x-1/2 rounded-lg bg-amber-900/80 px-3 py-1.5 text-xs text-amber-200 backdrop-blur-sm border border-amber-700/50">
            Offline mode — some features unavailable
          </div>
        )}
      </main>

      {/* Right Panel: Layers + Envelope */}
      {showLayerPanel && (
        <aside className="flex shrink-0 flex-col overflow-hidden border-l border-slate-700/50 bg-surface-elevated w-panel animate-slide-in-right">
          <div className="flex-1 overflow-y-auto p-4">
            <LayerSwitcher />
            <div className="my-3 h-px bg-slate-700/50" />
            <EnvelopeEditor />
            <div className="my-3 h-px bg-slate-700/50" />
            <Suspense fallback={<LazyFallback />}><ExportPanel parser={autoParser} /></Suspense>
          </div>
        </aside>
      )}

      {showShortcuts && <KeyboardShortcuts onClose={() => setShowShortcuts(false)} />}

      {/* Mobile/Tablet toggle buttons (only visible when panels are hidden) */}
      {!showProjectPanel && (
        <button
          onClick={() => useUiStore.getState().setShowProjectPanel(true)}
          className="absolute left-4 top-1/2 z-20 -translate-y-1/2 rounded-full bg-slate-800/90 p-3 text-white shadow-lg border border-slate-600/50 backdrop-blur-sm min-w-touch min-h-touch flex items-center justify-center"
          aria-label="Open project panel"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      )}

      {!showLayerPanel && (
        <button
          onClick={() => useUiStore.getState().setShowLayerPanel(true)}
          className="absolute right-4 top-1/2 z-20 -translate-y-1/2 rounded-full bg-slate-800/90 p-3 text-white shadow-lg border border-slate-600/50 backdrop-blur-sm min-w-touch min-h-touch flex items-center justify-center"
          aria-label="Open layer panel"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
        </button>
      )}
    </div>
  );
};

export const App: React.FC = () => {
  const session = useAuthStore((s) => s.session);
  const hydrated = useAuthStore((s) => s.hydrated);
  const setSession = useAuthStore((s) => s.setSession);
  const markHydrated = useAuthStore((s) => s.markHydrated);
  const status = selectStatus({ session, hydrated });

  useEffect(() => {
    // E2E smoke-test bypass (dev only): skip auth gate when query param present
    if (import.meta.env.DEV && new URLSearchParams(window.location.search).has('e2e-bypass-auth')) {
      setSession({ user: { id: 'e2e-user', email: 'e2e@example.com' } } as Session);
      markHydrated();
      return;
    }

    let unsubscribe: (() => void) | undefined;
    try {
      getSession()
        .then(setSession)
        .catch((err) => {
          console.error('[auth] getSession failed:', err);
          markHydrated();  // downgrade to anonymous so LoginModal renders
        });
      unsubscribe = onAuthChange((_event, session) => setSession(session));
    } catch (err) {
      console.error('[auth] supabase client init failed:', err);
      markHydrated();  // downgrade to anonymous on sync throw
    }
    return unsubscribe;
  }, [setSession, markHydrated]);

  if (status === 'unknown') return null;
  if (status === 'anonymous') return <LoginModal open />;

  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        <AppLayout />
      </ErrorBoundary>
    </QueryClientProvider>
  );
};
